/**
 * Atmosphere rims (Sprint 2, S2-08): a slightly larger sphere around a planet with additive Fresnel glow, bright on
 * the day side and faint on the night side. Colours come from `bodies.yaml` (`atmosphere`). All rims share one
 * uniform with the Sun's position in scene space (floating origin), so one update per frame serves every planet.
 */
import * as THREE from 'three';

/** Radius of the rim shell relative to the planet. */
const SHELL = 1.035;

/** Uniforms shared by all atmosphere materials. */
export const atmosphereUniforms = {
  uSunPos: { value: new THREE.Vector3() },
};

export function createAtmosphere(color: string, strength: number): THREE.Mesh {
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uSunPos: atmosphereUniforms.uSunPos,
      uColor: { value: new THREE.Color(color) },
      uStrength: { value: strength },
    },
    vertexShader: /* glsl */ `
      #include <common>
      #include <logdepthbuf_pars_vertex>
      varying vec3 vNormalW;
      varying vec3 vPosW;
      varying vec3 vNormalV;
      varying vec3 vViewDir;
      void main() {
        vec4 world = modelMatrix * vec4(position, 1.0);
        vPosW = world.xyz;
        vNormalW = normalize(mat3(modelMatrix) * normal);
        vec4 mv = viewMatrix * world;
        vNormalV = normalize(normalMatrix * normal);
        vViewDir = normalize(-mv.xyz);
        gl_Position = projectionMatrix * mv;
        #include <logdepthbuf_vertex>
      }`,
    fragmentShader: /* glsl */ `
      #include <common>
      #include <logdepthbuf_pars_fragment>
      uniform vec3 uSunPos;
      uniform vec3 uColor;
      uniform float uStrength;
      varying vec3 vNormalW;
      varying vec3 vPosW;
      varying vec3 vNormalV;
      varying vec3 vViewDir;
      void main() {
        #include <logdepthbuf_fragment>
        float facing = clamp(dot(normalize(vNormalV), normalize(vViewDir)), 0.0, 1.0);
        float rim = pow(1.0 - facing, 3.0);
        float sun = dot(normalize(vNormalW), normalize(uSunPos - vPosW));
        float day = smoothstep(-0.25, 0.6, sun);
        float a = rim * uStrength * (0.08 + 0.92 * day);
        gl_FragColor = vec4(uColor * a, 1.0);
        #include <colorspace_fragment>
      }`,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(SHELL, 48, 24), material);
  mesh.name = 'atmosphere';
  mesh.renderOrder = 1;
  return mesh;
}
